import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import supabase from "../../../../../utils/supabase";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import DialogResearcher from "../DialogResearcher";
import Fuse from "fuse.js";

const LOG_TAG = "[ResearcherSelector]";

const normalize = (s = "") =>
  s
    .toString()
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[,.:]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isUuid = (v = "") =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

export default function ResearcherSelector({
  value,
  onChange,
  label = "Search researcher",
}) {
  const [inputValue, setInputValue] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const requestIdRef = useRef(0);

  const loadSelected = useCallback(async () => {
    if (!value) {
      setSelectedValue(null);
      return;
    }
    const { data } = await supabase.from("researchers").select("*").eq("id", value).single();
    if (data) setSelectedValue(data);
  }, [value]);

  useEffect(() => {
    loadSelected();
  }, [loadSelected]);

  const searchResearchers = useCallback(
    async (raw) => {
      const term = (raw || "").trim();
      const reqId = ++requestIdRef.current;
      console.debug(LOG_TAG, "search start", { reqId, term });

      if (!term) {
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setResults([]);

      const normalizedInput = normalize(term);
      const tokens = normalizedInput.split(" ").filter(Boolean);

      let data = [];
      try {
        // Try RPC first
        const rpc = await supabase.rpc("search_researchers", { search_term: term });
        if (!rpc.error && Array.isArray(rpc.data)) data = rpc.data;
      } catch (e) {
        console.warn(LOG_TAG, "rpc exception", e);
      }

      if ((!data || data.length === 0) && tokens.length) {
        const first = tokens[0];
        const like = `%${first}%`;
        const ors = [
          `last_name.ilike.${like}`,
          `first_name.ilike.${like}`,
          `middle_name.ilike.${like}`,
          `name_particle.ilike.${like}`,
        ];
        if (isUuid(first)) ors.push(`id.eq.${first}`);
        const { data: fb, error } = await supabase
          .from("researchers")
          .select("*")
          .or(ors.join(","))
          .limit(100);
        if (!error && Array.isArray(fb)) data = fb;
      }

      if (reqId !== requestIdRef.current) {
        setLoading(false);
        return;
      }

      let list = Array.isArray(data) ? data : [];
      if (tokens.length && list.length) {
        const fuse = new Fuse(list, {
          keys: [
            (r) => `${r.last_name || ""} ${r.first_name || ""}`,
            (r) => `${r.first_name || ""} ${r.last_name || ""}`,
            (r) => `${r.last_name || ""}, ${r.first_name || ""}`,
            (r) => `${(r.first_name || "").slice(0, 1)} ${r.last_name || ""}`,
          ],
          threshold: 0.35,
          ignoreLocation: true,
          minMatchCharLength: 1,
        });
        const fuseResults = fuse.search(normalizedInput).map((r) => r.item);
        const containsTokens = (row) => {
          const haystack = normalize(
            `${row.last_name || ""} ${row.first_name || ""} ${row.middle_name || ""} ${row.name_particle || ""} ${
              (row.first_name || "").slice(0, 1)
            }`
          );
          return tokens.every((t) => haystack.includes(t));
        };
        const filtered = list.filter((r) => containsTokens(r));
        list = filtered.length ? filtered : fuseResults.length ? fuseResults : list;
      }

      console.debug(LOG_TAG, "search done", { reqId, results: list.length });
      setResults(list);
      setLoading(false);
    },
    []
  );

  const handleSelect = (row) => {
    setSelectedValue(row);
    onChange?.(row?.id || null);
    setSearchDialogOpen(false);
  };

  const renderResultLabel = (row) => {
    const parts = [
      row.last_name || "",
      row.first_name ? `, ${row.first_name}` : "",
      row.middle_name ? ` ${row.middle_name}` : "",
      row.name_particle ? ` ${row.name_particle}` : "",
    ]
      .join("")
      .trim();
    return parts || row.id;
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<PersonSearchIcon />}
        onClick={() => {
          setSearchDialogOpen(true);
          setResults([]);
          setInputValue("");
        }}
        sx={{ mb: 1 }}
      >
        SEARCH RESEARCHER
      </Button>

      <Dialog
        open={searchDialogOpen}
        onClose={() => setSearchDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { height: 520 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Search Researcher</Typography>
          <Button
            size="small"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add/Edit Researcher
          </Button>
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <TextField
            label={label}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                searchResearchers(inputValue);
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Search">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => searchResearchers(inputValue)}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={18} /> : <SearchIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Results: {results.length}
          </Typography>
          <Box sx={{ flex: 1, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            {results.length === 0 && !loading ? (
              <Typography variant="body2" sx={{ p: 2 }}>
                No results. Try a different query.
              </Typography>
            ) : (
              <List dense>
                {results.map((row) => (
                  <ListItemButton key={row.id} onClick={() => handleSelect(row)}>
                    <ListItemText
                      primary={renderResultLabel(row)}
                      secondary={row.orcid || row.id}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {addDialogOpen && (
        <DialogResearcher
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          researcherId={null}
        />
      )}
    </>
  );
}
